import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, ListItemText, Menu, MenuItem } from '@mui/material';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { apiClient } from '@/lib/apiClient';
import { bulkCommandOffer, type BulkCommandAvailability, type BulkCommandConfig } from '@/lib/bulkCommand';

interface BulkCommandsMenuProps {
  commands: BulkCommandConfig[];
  /** POST endpoint answering which selected rows can run each command, e.g. `/api/sales/order/bulk/available-actions`. */
  availabilityEndpoint: string;
  selectedIds: string[];
  disabled: boolean;
  /** The chosen command, with what the selected rows answered about it; null when the question failed. */
  onChoose: (command: BulkCommandConfig, availability: BulkCommandAvailability | null) => void;
}

/**
 * The list's Actions menu over the selected rows (docs/15-designspecs/Bulk-Edit-And-Actions.md §8.1). When it opens it
 * asks the server how many of the selected rows can run each command — each row answered by its own available-actions,
 * as the record's Actions menu is — and offers a command no row can run as unavailable, with the reasons, and one some
 * rows can run with how many. The chosen command carries that answer, so the run is offered on exactly those rows.
 */
export function BulkCommandsMenu({ commands, availabilityEndpoint, selectedIds, disabled, onChoose }: BulkCommandsMenuProps) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [availability, setAvailability] = useState<BulkCommandAvailability[] | null>(null);
  const [asking, setAsking] = useState(false);
  // Each opening asks about the rows selected then; only the latest question's answer is used. An earlier one
  // arriving after it names other rows, and the run would be offered on those.
  const latestQuestion = useRef(0);

  const open = async (target: HTMLElement) => {
    const question = ++latestQuestion.current;
    setAnchor(target);
    setAvailability(null);
    setAsking(true);
    try {
      const response = await apiClient.post<{ commands: BulkCommandAvailability[] }>(availabilityEndpoint, { ids: selectedIds });
      if (question === latestQuestion.current) setAvailability(response.data.commands);
    } catch (error) {
      // The menu still offers every command: each row's command decides when it runs and reports a refusal.
      console.warn('Could not ask which selected rows can run each command', error);
    } finally {
      if (question === latestQuestion.current) setAsking(false);
    }
  };

  return (
    <>
      <Button
        endIcon={<ArrowDropDown />}
        disabled={disabled}
        onClick={(event) => void open(event.currentTarget)}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        {t('actions.actions')}
        {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {commands.map((command) => {
          const offer = bulkCommandOffer(command, availability, t);
          return (
            <MenuItem
              key={command.key}
              disabled={asking || offer.disabled}
              onClick={() => {
                setAnchor(null);
                onChoose(command, availability?.find((a) => a.command === command.command) ?? null);
              }}
            >
              <ListItemText primary={offer.label} secondary={offer.reasons.length > 0 ? offer.reasons.join(' ') : undefined} />
              {asking && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
